import { CountOperator, getRelationshipDirection } from '@reach/shared-types/conditions';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { generateId } from '@/lib/generate-id';
import { Plus } from 'lucide-react';
import React from 'react';
import { getRelationshipOptionsForSubject } from '@reach/shared-types/segment-schema-graph';
import type { SchemaDisplayMetadata } from '@reach/shared-types/schema-mappings';
import { SchemaIcon } from '@/components/schema-display/SchemaIcon';
import type { SchemaRelationship } from '../../engage-segments-tab/types';
import type { BuilderRelatedResourceCondition } from './segment-builder.types';
import { titleCaseSchemaName } from './segment-builder.utils';
import { useSegmentSchemaCatalog } from './useSegmentSchemaCatalog';
import styles from './SegmentVisualBuilder.module.css';

interface AddRelatedResourceConditionProps {
  subjectSchemaId: string | null;
  addRelatedResourceCondition: (condition: BuilderRelatedResourceCondition) => void;
  disabled?: boolean;
}

/** A single way to reach a related schema, labelled by the relationship it travels. */
interface RelationshipRoute {
  relationship: SchemaRelationship;
  label: string;
  cardinalityAndDirection: string;
}

/** One schema heading plus every route that reaches it from the subject. */
interface RelatedSchemaGroup {
  schemaId: string;
  name: string;
  displayMetadata?: SchemaDisplayMetadata | null;
  routes: RelationshipRoute[];
  connectionCountLabel?: string;
}

export const AddRelatedResourceCondition: React.FC<AddRelatedResourceConditionProps> = ({
  subjectSchemaId,
  addRelatedResourceCondition,
  disabled = false,
}) => {
  const { schemaCatalog, getSchemaById } = useSegmentSchemaCatalog();
  if (!schemaCatalog) return null;

  const relationshipOptions = subjectSchemaId
    ? getRelationshipOptionsForSubject({ graph: schemaCatalog, subjectSchemaId })
    : [];

  const addConditionForRelationship = (relationship: SchemaRelationship) => {
    if (!subjectSchemaId) return;

    const { foreignKeyOn, relatedSchemaId } = getRelationshipDirection({
      relationship,
      subjectSchemaId,
    });
    addRelatedResourceCondition({
      id: generateId(),
      type: 'related_resource',
      schemaId: relatedSchemaId,
      schemaName: getSchemaById(relatedSchemaId)?.name ?? 'Related resource',
      foreignKeyPath: relationship.foreignKeyPath,
      foreignKeyOn,
      countOperator: CountOperator.GTE,
      countValue: 1,
      conditions: [],
      isExpanded: true,
    });
  };

  const subjectName = titleCaseSchemaName(
    subjectSchemaId ? (getSchemaById(subjectSchemaId)?.name ?? 'record') : 'record',
  );

  // Bucket by the schema each relationship resolves to, so the object is picked before the route.
  const groupsBySchemaId = new Map<string, RelatedSchemaGroup>();
  if (subjectSchemaId) {
    for (const relationship of relationshipOptions) {
      const { relatedSchemaId, linkedResourceOwnsForeignKey } = getRelationshipDirection({
        relationship,
        subjectSchemaId,
      });
      const cardinality = relationship.cardinality === 'many' ? 'many' : 'one';
      const direction = linkedResourceOwnsForeignKey
        ? `references ${subjectName}`
        : `${subjectName} references it`;
      const route: RelationshipRoute = {
        relationship,
        label: `via ${relationship.label}`,
        cardinalityAndDirection: `${cardinality} · ${direction}`,
      };
      const existing = groupsBySchemaId.get(relatedSchemaId);
      if (existing) {
        existing.routes.push(route);
        continue;
      }
      const relatedSchema = getSchemaById(relatedSchemaId);
      groupsBySchemaId.set(relatedSchemaId, {
        schemaId: relatedSchemaId,
        name: titleCaseSchemaName(relatedSchema?.name ?? 'Related resource'),
        displayMetadata: relatedSchema?.displayMetadata,
        routes: [route],
      });
    }
  }

  const relatedSchemaGroups = [...groupsBySchemaId.values()].map(group => ({
    ...group,
    connectionCountLabel: group.routes.length > 1 ? `${group.routes.length} ways to connect` : undefined,
  }));

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        className={styles.addConditionBtn}
        disabled={disabled || relationshipOptions.length === 0}>
        <Plus size={14} />
        Related Record
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content className={styles.addMenu} align="start" sideOffset={4}>
          {relatedSchemaGroups.map(group => (
            <DropdownMenu.Group key={group.schemaId} className={styles.addMenuGroup}>
              <DropdownMenu.Label className={styles.addMenuGroupLabel}>
                <SchemaIcon displayMetadata={group.displayMetadata} size={13} />
                {group.name}
                {group.connectionCountLabel && (
                  <span className={styles.addMenuGroupCount}>{group.connectionCountLabel}</span>
                )}
              </DropdownMenu.Label>
              {group.routes.map(route => (
                <DropdownMenu.Item
                  key={route.relationship.id}
                  className={styles.addMenuItem}
                  onSelect={addConditionForRelationship.bind(null, route.relationship)}>
                  {route.label}
                  <span className={styles.addMenuItemHint}>{route.cardinalityAndDirection}</span>
                </DropdownMenu.Item>
              ))}
            </DropdownMenu.Group>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};
